import React, { useMemo } from 'react';
import ReactDOM from 'react-dom';

const overlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,.6)',
    zIndex: 1100,
};

const modalStyle = {
    position: 'fixed',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    backgroundColor: '#fff',
    padding: '20px',
    borderRadius: '8px',
    zIndex: 1101,
    width: '80%',
    maxWidth: '420px',
    boxShadow: '0 6px 24px rgba(0,0,0,0.2)',
};

const rowStyle = { marginBottom: '14px' };
const labelRowStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '14px',
    fontWeight: 600,
    marginBottom: '4px',
};

const hexToRgb = (hex) => {
    let h = (hex || '').replace('#', '');
    if (h.length === 3) h = h.split('').map((c) => c + c).join('');
    if (h.length !== 6) return { r: 0, g: 0, b: 0 };
    return {
        r: parseInt(h.slice(0, 2), 16),
        g: parseInt(h.slice(2, 4), 16),
        b: parseInt(h.slice(4, 6), 16),
    };
};

const rgbToHex = (r, g, b) => {
    const to = (n) => Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, '0');
    return `#${to(r)}${to(g)}${to(b)}`;
};

// r,g,b in 0..255  →  h in 0..360, s in 0..100, v in 0..100
export const rgbToHsv = (r, g, b) => {
    const rn = r / 255, gn = g / 255, bn = b / 255;
    const max = Math.max(rn, gn, bn);
    const min = Math.min(rn, gn, bn);
    const d = max - min;
    let h = 0;
    if (d !== 0) {
        if (max === rn) h = ((gn - bn) / d) % 6;
        else if (max === gn) h = (bn - rn) / d + 2;
        else h = (rn - gn) / d + 4;
        h *= 60;
        if (h < 0) h += 360;
    }
    const s = max === 0 ? 0 : (d / max) * 100;
    const v = max * 100;
    return { h, s, v };
};

// h in 0..360, s/v in 0..100  →  r,g,b in 0..255
export const hsvToRgb = (h, s, v) => {
    const sn = s / 100, vn = v / 100;
    const c = vn * sn;
    const hh = ((h % 360) + 360) % 360 / 60;
    const x = c * (1 - Math.abs((hh % 2) - 1));
    let r = 0, g = 0, b = 0;
    if (hh < 1) { r = c; g = x; }
    else if (hh < 2) { r = x; g = c; }
    else if (hh < 3) { g = c; b = x; }
    else if (hh < 4) { g = x; b = c; }
    else if (hh < 5) { r = x; b = c; }
    else { r = c; b = x; }
    const m = vn - c;
    return { r: (r + m) * 255, g: (g + m) * 255, b: (b + m) * 255 };
};

// Apply a symmetric random offset to each channel.
// hueAmount in 0..180 (degrees), satAmount/valAmount in 0..100.
export const jitterHex = (hex, hueAmount, satAmount, valAmount) => {
    if (!hueAmount && !satAmount && !valAmount) return hex;
    const { r, g, b } = hexToRgb(hex);
    const { h, s, v } = rgbToHsv(r, g, b);
    const jh = h + (Math.random() * 2 - 1) * hueAmount;
    const js = Math.max(0, Math.min(100, s + (Math.random() * 2 - 1) * satAmount));
    const jv = Math.max(0, Math.min(100, v + (Math.random() * 2 - 1) * valAmount));
    const out = hsvToRgb(jh, js, jv);
    return rgbToHex(out.r, out.g, out.b);
};

function Slider({ label, value, max, onChange, suffix }) {
    return (
        <div style={rowStyle}>
            <div style={labelRowStyle}>
                <span>{label}</span>
                <span>±{value}{suffix}</span>
            </div>
            <input
                type="range"
                min={0}
                max={max}
                value={value}
                onChange={(e) => onChange(Number(e.target.value))}
                style={{ width: '100%' }}
            />
        </div>
    );
}

const modeButtonStyle = (active) => ({
    flex: 1,
    padding: '8px 12px',
    fontSize: '14px',
    fontWeight: active ? 700 : 500,
    borderRadius: '6px',
    border: active ? '2px solid #4CAF50' : '1px solid #bbb',
    background: active ? '#E8F5E9' : '#fff',
    color: '#222',
    cursor: 'pointer',
});

function HsvJitterModal({
    open,
    onClose,
    color,
    hueJitter,
    satJitter,
    valJitter,
    setHueJitter,
    setSatJitter,
    setValJitter,
    mode,
    setMode,
}) {
    // Stable preview swatches: regenerated whenever the inputs change.
    const swatches = useMemo(() => {
        if (!open) return [];
        const list = [];
        for (let i = 0; i < 12; i += 1) {
            list.push(jitterHex(color, hueJitter, satJitter, valJitter));
        }
        return list;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, color, hueJitter, satJitter, valJitter]);

    if (!open) return null;

    const portalEl = document.getElementById('portal');
    if (!portalEl) return null;

    return ReactDOM.createPortal(
        <>
            <div style={overlayStyle} onClick={onClose} />
            <div style={modalStyle} className="hsv-jitter-modal">
                <div style={{ fontSize: '18px', fontWeight: 700, marginBottom: '12px', textAlign: 'center' }}>
                    Color Jitter
                </div>
                <div style={{ fontSize: '13px', color: '#555', marginBottom: '14px', textAlign: 'center' }}>
                    Randomly shifts color within these ranges.
                </div>

                <div style={{ display: 'flex', gap: '8px', marginBottom: '14px' }}>
                    <button
                        type="button"
                        onClick={() => setMode('stroke')}
                        style={modeButtonStyle(mode !== 'paint')}
                        title="One jittered color per stroke"
                    >
                        Per stroke
                    </button>
                    <button
                        type="button"
                        onClick={() => setMode('paint')}
                        style={modeButtonStyle(mode === 'paint')}
                        title="Re-jitter every frame (freehand squiggle only)"
                    >
                        Per paint
                    </button>
                </div>

                <Slider label="Hue" value={hueJitter} max={180} onChange={setHueJitter} suffix="°" />
                <Slider label="Saturation" value={satJitter} max={100} onChange={setSatJitter} suffix="%" />
                <Slider label="Value" value={valJitter} max={100} onChange={setValJitter} suffix="%" />

                <div style={{ fontSize: '13px', fontWeight: 600, marginTop: '8px', marginBottom: '6px' }}>
                    Preview
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '16px' }}>
                    {swatches.map((hex, i) => (
                        <div
                            key={i}
                            style={{
                                width: '26px',
                                height: '26px',
                                borderRadius: '50%',
                                background: hex,
                                border: '1px solid #888',
                            }}
                        />
                    ))}
                </div>

                <div style={{ display: 'flex', gap: '8px', justifyContent: 'space-between' }}>
                    <button
                        type="button"
                        onClick={() => { setHueJitter(0); setSatJitter(0); setValJitter(0); }}
                        style={{
                            flex: 1,
                            padding: '10px',
                            fontSize: '14px',
                            border: '1px solid #bbb',
                            borderRadius: '6px',
                            background: '#fff',
                            cursor: 'pointer',
                        }}
                    >
                        Reset
                    </button>
                    <button
                        type="button"
                        onClick={onClose}
                        style={{
                            flex: 1,
                            padding: '10px',
                            fontSize: '14px',
                            border: '2px solid #4CAF50',
                            borderRadius: '6px',
                            background: '#E8F5E9',
                            cursor: 'pointer',
                            fontWeight: 700,
                        }}
                    >
                        Done
                    </button>
                </div>
            </div>
        </>,
        portalEl,
    );
}

export default HsvJitterModal;
